import { useAuth0 } from '@auth0/auth0-react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import ButtonGroup from '@mui/material/ButtonGroup';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid2';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import PropTypes from 'prop-types';
import React, {
    useContext,
    useState,
} from 'react';
import { toast } from 'react-toastify';

import { getAuthToken } from '../../auth/authHelpers';
import { DAYS_LIST } from '../../constants/daysList';
import { LANGUAGES } from '../../constants/LanguageList';
import {
    POL_THEME,
    ThemeContext,
} from '../../constants/theme';
import customerDataRequest from '../../services/api/customer-data';
import {
    getDateObjFromHhMmSsString,
    getHoursMinutes,
    getUtcDateTime,
    sanitizeLuxonDateValue,
} from '../../utils/date';

export const ADD_TIME = 'ADD TIME';
export const EDIT_TIME = 'EDIT TIME';

const stylesProps = {
    textProps: {
        font: 'normal normal normal 14px/62px "Open Sans"',
        fontFamily: '"Open Sans", tahoma, verdana, sans-serif',
    },
    serviceTypeProps: {
        fieldProps: { padding: '0px 26px 10px 12px', minWidth: '140px' },
    },
    btnProps: {
        textAlign: 'left',
        letterSpacing: '0px',
        textTransform: 'capitalize',
    },
    cancelBtnProps: {
        ml: 1.0,
        color: '#264550',
    },
    addBtnProps: {
        backgroundColor: '#264550',
        color: '#FFFFFF',
    },
    groupedBtnPros: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'right',
        '& > *': {
            mt: 0.3,
        },
        btn: {
            border: '1px solid #C7C6C6',
            color: '#3C3B3B',
            textTransform: 'none',
        },
    },
};

const SERVICE_TYPES = [
    'Weekend',
    'Weekday',
    'Special',
    'Confession',
];

const HOUR_TYPES = [
    'Weekday',
    'Weekend',
    'Other',
];

const MassTimeAddEdit = ({
    formInputProps,
    formSubmitCallback,
    orgSalesforceId,
    isHidden,
}) => {
    const themeContext = useContext(ThemeContext);
    const { getAccessTokenSilently } = useAuth0();

    const themeDefaultType = themeContext === POL_THEME ? 'Weekend' : 'Weekday';
    const themeDefaultDay = themeContext === POL_THEME ? 'Saturday' : 'Monday';

    // Get input values if editing existing time or use defaults if adding new time
    const inputLanguage = formInputProps.languages
        ? formInputProps.languages.replace(/\s*,\s*/g, ',').split(',') // remove space and convert to string for auto complete component
        : ['English'];
    const inputServiceType = formInputProps.type
        ? formInputProps.type
        : themeDefaultType;
    const inputDayOfWeek = formInputProps.day_of_week
        ? formInputProps.day_of_week
        : themeDefaultDay;
    const inputStartTime = formInputProps.start_time
        ? getDateObjFromHhMmSsString(formInputProps.start_time) // converting back to date object for TimePicker with UTC time zone
        : new Date();
    const inputEndTime = formInputProps.end_time
        ? getDateObjFromHhMmSsString(formInputProps.end_time) // converting back to date object for TimePicker with UTC time zone
        : null;
    const inputAdditionalDetails = formInputProps.additional_details || '';

    const [selectedLanguage, setSelectedLanguage] = useState(inputLanguage);
    const [selectedServiceType, setSelectedServiceType] = useState(inputServiceType);
    const [selectedDay, setSelectedDay] = useState(inputDayOfWeek);
    const [selectedStartTime, setSelectedStartTime] = useState(inputStartTime);
    const [selectedEndTime, setSelectedEndTime] = useState(inputEndTime);
    const [selectedAdditionalDetails, setSelectedAdditionalDetails] = useState(inputAdditionalDetails);

    const handleServiceTypeSelection = (type) => {
        // setting default day
        if (type === 'Weekend') {
            setSelectedDay('Saturday');
        } else if (type === 'Weekday') {
            setSelectedDay('Monday');
        }
        setSelectedServiceType(type);
    };

    const serviceTypeFields = (id) => (
        <FormControl
            id="serviceTypeFieldsId"
            key="serviceTypeFieldsKey"
            sx={stylesProps.serviceTypeProps.fieldProps}
        >
            <Select
                labelId="serviceType"
                input={<OutlinedInput id={id} />}
                value={selectedServiceType}
                label="Service Type"
                onChange={(event) => handleServiceTypeSelection(event.target.value)}
                disabled={isHidden}
            >
                {/* change the array of types based on theme */}
                {(themeContext === POL_THEME ? SERVICE_TYPES : HOUR_TYPES).map((type) => (
                    <MenuItem
                        key={type}
                        value={type}
                    >
                        {type}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const daysFields = () => {
        let availableDays;
        if (selectedServiceType === 'Weekend') {
            availableDays = DAYS_LIST.filter((d) => d.label === 'Sat' || d.label === 'Sun');
        } else if (selectedServiceType === 'Weekday') {
            availableDays = DAYS_LIST.filter((d) => !(d.label === 'Sat' || d.label === 'Sun'));
        } else {
            availableDays = DAYS_LIST;
        }

        return (
            <Box sx={stylesProps.groupedBtnPros}>
                <ButtonGroup
                    size="large"
                    sx={{
                        height: '53px',
                        marginTop: '-1.6px',
                    }}
                >
                    {availableDays.map((d) => {
                        const isSelected = selectedDay === d.day;
                        return (
                            <Button
                                key={d.day}
                                sx={{
                                    ...stylesProps.textProps,
                                    ...stylesProps.groupedBtnPros.btn,
                                    backgroundColor: isSelected ? '#264550' : '#FFFFFF 0% 0% no-repeat padding-box',
                                    color: isSelected ? 'white' : 'inherit',
                                    opacity: isSelected ? 0.8 : 1,
                                }}
                                variant={isSelected ? 'contained' : 'outlined'}
                                value={d.day}
                                disableRipple
                                disableFocusRipple
                                disableElevation
                                onClick={(event) => setSelectedDay(event.target.value)}
                                disabled={isHidden}
                            >
                                {d.label}
                            </Button>
                        );
                    })}
                </ButtonGroup>
            </Box>
        );
    };

    const handleTimeSelection = (timeFieldType, time) => {
        if (timeFieldType === 'start') {
            setSelectedStartTime(time);
        }
        if (timeFieldType === 'end') {
            setSelectedEndTime(time);
        }
    };

    const timeFields = (timeFieldType, id) => {
        let value = '';
        if (timeFieldType === 'start') {
            value = selectedStartTime;
        }
        if (timeFieldType === 'end') {
            value = selectedEndTime;
        }

        return (
            <LocalizationProvider dateAdapter={AdapterLuxon}>
                <TimePicker
                    id={id}
                    value={sanitizeLuxonDateValue(value)}
                    onChange={(time) => {
                        handleTimeSelection(timeFieldType, time.toJSDate());
                    }}
                    slotProps={{
                        textfield: {
                            sx: {
                                width: '140px',
                            },
                        },
                    }}
                    disabled={isHidden}
                />
            </LocalizationProvider>
        );
    };

    const languagesField = () => (
        <Stack
            ml="15px"
            mt="15px"
            width="400px"
        >
            <InputLabel htmlFor="language">
                Languages
            </InputLabel>
            <Autocomplete
                multiple
                disableClearable
                filterSelectedOptions
                clearIcon={null}
                id="language"
                options={LANGUAGES}
                getOptionLabel={(option) => option}
                value={selectedLanguage}
                onChange={(event, value) => setSelectedLanguage(value)}
                renderInput={(params) => (
                    <TextField
                        error={selectedLanguage.length < 1}
                        {...params}
                    />
                )}
                disabled={isHidden}
            />
        </Stack>
    );

    const createMassTime = async (massTimeData) => {
        const token = await getAuthToken(getAccessTokenSilently);

        try {
            await customerDataRequest('post', `/organizations/${orgSalesforceId}/mass-times`, token, massTimeData);

            toast.success(`${themeContext === POL_THEME ? 'Mass Times' : 'Hours'} Added`);
        } catch (error) {
            toast.error(`There was an error adding ${themeContext === POL_THEME ? 'Mass Times' : 'Hours'} details`);
            throw error;
        }

        await formSubmitCallback(token);
    };

    const editMassTime = async (massTimeData) => {
        const token = await getAuthToken(getAccessTokenSilently);

        try {
            const url = `/organizations/${orgSalesforceId}/mass-times/${formInputProps.id}`;
            await customerDataRequest('patch', url, token, massTimeData);
            toast.success(`${themeContext === POL_THEME ? 'Mass Times' : 'Hours'} Updated`);
        } catch (error) {
            toast.error(`There was an error editing ${themeContext === POL_THEME ? 'Mass Times' : 'Hours'} details`);
            throw error;
        }

        await formSubmitCallback(token);
    };

    const handleSubmit = async () => {
        const currentDateTime = getUtcDateTime(new Date());
        const massTimeData = {
            type: selectedServiceType,
            day_of_week: selectedDay,
            start_time: getHoursMinutes(selectedStartTime), // 20:58:47
            end_time: getHoursMinutes(selectedEndTime),
            languages: selectedLanguage.join(', '),
            additional_details: selectedAdditionalDetails,
            title: 'Mass Times',
            created_at: currentDateTime,
            updated_at: currentDateTime,
            timezone: 'UTC',
        };

        if (formInputProps.action === ADD_TIME) {
            await createMassTime(massTimeData);
        } else {
            await editMassTime(massTimeData);
        }
    };

    return (
        <>
            <h2 style={{ margin: '15px' }}>
                {formInputProps.action}
            </h2>
            <Box>
                <Grid container>
                    <Box>
                        <InputLabel
                            htmlFor="serviceType"
                            sx={{
                                padding: '0px 10px  0px 15px',
                            }}
                        >
                            {`${themeContext === POL_THEME ? 'Service' : 'Hour'} Type`}
                        </InputLabel>
                        {serviceTypeFields('serviceType')}
                    </Box>
                    <Box ml="15px">
                        <InputLabel
                            id="daysOfTheWeek"
                            sx={{
                                fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                            }}
                        >
                            Days of the Week
                        </InputLabel>
                        {daysFields()}
                    </Box>
                </Grid>
                <Grid
                    container
                    sx={{
                        paddingTop: '10px',
                    }}
                >
                    <Box sx={{ paddingLeft: '15px', paddingRight: '25px', paddingBottom: '15px' }}>
                        <InputLabel
                            htmlFor="startTime"
                            sx={{
                                fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                            }}
                        >
                            Start Time
                        </InputLabel>
                        {timeFields('start', 'startTime')}
                    </Box>
                    <Box
                        sx={{
                            paddingLeft: '15px',
                        }}
                    >
                        <InputLabel
                            htmlFor="endTime"
                            sx={{
                                fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                            }}
                        >
                            End Time - (
                            <i>Optional)</i>
                        </InputLabel>
                        {timeFields('end', 'endTime')}
                    </Box>
                </Grid>
                <Grid>
                    {languagesField()}
                </Grid>
                <Grid
                    ml="15px"
                    mt="15px"
                    width="400px"
                >
                    <InputLabel htmlFor="additionalDetails">
                        Additional Details
                    </InputLabel>
                    <TextField
                        id="additionalDetails"
                        variant="outlined"
                        fullWidth
                        value={selectedAdditionalDetails}
                        onChange={(event) => setSelectedAdditionalDetails(event.target.value)}
                        slotProps={{
                            input: {
                                maxLength: 50,
                            },
                        }}
                        disabled={isHidden}
                    />
                </Grid>
                <Grid
                    ml="15px"
                    mt="15px"
                    width="400px"
                >
                    <Button
                        disabled={!!((!selectedStartTime || selectedLanguage.length < 1)) || isHidden}
                        sx={{
                            ...stylesProps.btnProps,
                            ...stylesProps.addBtnProps,
                            ':hover': {
                                bgcolor: '#008085',
                            },
                        }}
                        onClick={() => handleSubmit()}
                    >
                        {formInputProps.action.toLocaleLowerCase()}
                    </Button>
                    <Button
                        sx={{
                            ...stylesProps.btnProps,
                            ...stylesProps.cancelBtnProps,
                        }}
                        onClick={() => formSubmitCallback()}
                        disabled={isHidden}
                    >
                        Cancel
                    </Button>
                </Grid>
            </Box>
        </>
    );
};

MassTimeAddEdit.propTypes = {
    formInputProps: PropTypes.objectOf(PropTypes.any).isRequired,
    formSubmitCallback: PropTypes.func.isRequired,
    orgSalesforceId: PropTypes.string.isRequired,
    isHidden: PropTypes.bool,
};

MassTimeAddEdit.defaultProps = {
    isHidden: false,
};

export default MassTimeAddEdit;
